import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface ContaFinanceiraType {
    id: number;
    name: string;    
    openingBalance: number;
    empresaId: number;
}

export interface CreateContaFinanceiraType {
    name: string;
    openingBalance: number;
    empresaId: number;
}

export interface UpdateContaFinanceiraType {
    id: number;
    name: string;
    openingBalance: number;
}

export default function useContaFinanceira() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listContaFinanceira, setListContaFinanceira] = useState<ContaFinanceiraType[]>([]);
    const [listContaFinanceiraByEmpresa, setListContaFinanceiraByEmpresa] = useState<ContaFinanceiraType[]>([]);
    const [contaFinanceiraPageCount, setContaFinanceiraPageCount] = useState<number>(0);
    const [initialListContaFinanceira, setInitialListContaFinanceira] = useState<ContaFinanceiraType[]>([]);
    const [contaFinanceira, setContaFinanceira] = useState<ContaFinanceiraType>({
        id: 0,
        name: "",
        openingBalance: 0,
        empresaId: 0,
    });

    function createContaFinanceira(createContaFinanceiraType: CreateContaFinanceiraType): Promise<CreateContaFinanceiraType | null> {
        setLoading(true);
        return api
            .post(`/ContaFinanceira/create`, createContaFinanceiraType)
            .then((response) => {
                const createdContaFinanceira: CreateContaFinanceiraType = response.data;
                showToast("success", "Conta financeira criada com sucesso.");
                setLoading(false);
                return createdContaFinanceira;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar conta financeira. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar conta financeira:", error);
                return null;
            });
    }

    function getAllContaFinanceiraPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<ContaFinanceiraType[] | null> {
        setLoading(true);
        let url = `/ContaFinanceira/getAllContaFinanceiraPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListContaFinanceira(response.data.result);
                setInitialListContaFinanceira(response.data.result);
                setContaFinanceiraPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar contas do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar contas do financeiro:", error);
                return null;
            });
    }

    function updateContaFinanceira(updateContaFinanceiraData: UpdateContaFinanceiraType): Promise<UpdateContaFinanceiraType | null> {
        setLoading(true);
        return api
            .put(`/ContaFinanceira/${updateContaFinanceiraData.id}`, updateContaFinanceiraData)
            .then((response) => {
                showToast("success", "Conta atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar conta:", error);
                return null;
            });
    }

    function deleteContaFinanceira(contaFinanceiraId: number): Promise<ContaFinanceiraType | null> {
        setLoading(true);
        return api
            .delete(`/ContaFinanceira/${contaFinanceiraId}`)
            .then(() => {
                setContaFinanceira({
                    id: 0,
                    name: "",
                    openingBalance: 0,
                    empresaId: 0,
                });
                showToast("success", "Conta deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar conta:", error);
                return null;
            });
    }

    function handleGetContaFinanceiraByEmpresaId(empresaId: number): Promise<ContaFinanceiraType[] | null> {
            setLoading(true);
            return api
                .get(`/ContaFinanceira/getContaFinanceiraByEmpresaId?empresaId=${empresaId}`)
                .then((response) => {
                    const contaFinanceira: ContaFinanceiraType[] = response.data;
                    setListContaFinanceiraByEmpresa(contaFinanceira);
                    setLoading(false);
                    return contaFinanceira;
                })
                .catch((error) => {
                    if (error?.response?.status === 401) {
                        handleLogout();
                        showToast(
                            "error",
                            "Sessão desconectada. Favor login novamente.",
                            error
                        );
                    } else {
                        showToast(
                            "error",
                            "Erro ao buscar informações das contas do financeiro. Verifique novamente mais tarde.",
                            error
                        );
                    }
                    setLoading(false);
                    console.error("Erro ao buscar informações das contas do financeiro:", error);
                    return null;
                });
        }

    return {
        createContaFinanceira,
        getAllContaFinanceiraPaged,
        updateContaFinanceira,
        deleteContaFinanceira,
        setListContaFinanceira,
        listContaFinanceira,
        setContaFinanceiraPageCount,
        contaFinanceiraPageCount,
        setInitialListContaFinanceira,
        initialListContaFinanceira,
        setContaFinanceira,
        contaFinanceira,
        handleGetContaFinanceiraByEmpresaId,
        setListContaFinanceiraByEmpresa,
        listContaFinanceiraByEmpresa,
    };
}